import React from "react";
import Box from "@mui/joy/Box";
import Typography from "@mui/joy/Typography";
import ToggleButtonGroup from "@mui/joy/ToggleButtonGroup";
import Button from "@mui/joy/Button";
import { LineChart } from "@mui/x-charts/LineChart";
import { AnalyticsFilters, AnalyticsFiltersState } from "../components/AnalyticsFilters";
import {
    AnalyticsCard,
    BlockError,
    BlockLoading,
    EmptyState,
    MetricHint,
    WatchSignals,
} from "../components/AnalyticsShared";
import { useAsyncBlock } from "../hooks";
import { bidsTimeStatisticApi } from "../../bidsApi";
import { BidTimeStatistic } from "../../../../models/bidAnalytics";
import { formatInt, formatMoneyKopecks, toNum } from "../format";
import { formatBucketLabel, rollupSeries } from "../rollup";
import { Game } from "../../../../models/games";

type Mode = "count" | "sum";

type Props = {
    isActive: boolean;
    filters: AnalyticsFiltersState;
    onFiltersChange: (next: AnalyticsFiltersState) => void;
    games: Game[];
    gamesLoading: boolean;
};

export function DemandDynamicsTab(props: Props) {
    const { isActive, filters, onFiltersChange, games, gamesLoading } = props;
    const [mode, setMode] = React.useState<Mode>("count");
    const [isSmall, setIsSmall] = React.useState(false);

    React.useEffect(() => {
        const onResize = () => setIsSmall(window.innerWidth < 600);
        onResize();
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    const [state, refresh] = useAsyncBlock<BidTimeStatistic[]>(
        () =>
            bidsTimeStatisticApi({
                from: filters.from,
                to: filters.to,
                gameId: filters.gameId,
            }),
        isActive,
        [filters.from, filters.to, filters.gameId]
    );

    const dataset = React.useMemo(() => {
        if (!state.data) {
            return [];
        }
        const rolled = rollupSeries(state.data, filters.granularity, {
            sumKeys: [
                "totalCount",
                "totalSum",
                "totalBought",
                "totalBoughtSum",
                "csCount",
                "csSum",
                "dota2Count",
                "dota2Sum",
                "tf2Count",
                "tf2Sum",
                "rustCount",
                "rustSum",
                "giftCount",
                "giftSum",
            ],
        });
        return rolled.map((row) => ({
            x: row.x,
            total: mode === "count" ? row.totalCount : toNum(row.totalSum) / 100,
            bought: mode === "count" ? row.totalBought : toNum(row.totalBoughtSum) / 100,
            cs: mode === "count" ? row.csCount : toNum(row.csSum) / 100,
            dota: mode === "count" ? row.dota2Count : toNum(row.dota2Sum) / 100,
            tf2: mode === "count" ? row.tf2Count : toNum(row.tf2Sum) / 100,
            rust: mode === "count" ? row.rustCount : toNum(row.rustSum) / 100,
            gift: mode === "count" ? row.giftCount : toNum(row.giftSum) / 100,
        }));
    }, [state.data, mode, filters.granularity]);

    const totals = React.useMemo(() => {
        if (!state.data?.length) {
            return null;
        }
        return state.data.reduce(
            (acc, row) => ({
                count: acc.count + row.totalCount,
                sum: acc.sum + toNum(row.totalSum),
                bought: acc.bought + row.totalBought,
                boughtSum: acc.boughtSum + toNum(row.totalBoughtSum),
            }),
            { count: 0, sum: 0, bought: 0, boughtSum: 0 }
        );
    }, [state.data]);

    const valueFmt = (v: number | null) => {
        if (v == null) {
            return "—";
        }
        return mode === "count" ? formatInt(v) : `${v.toLocaleString("ru-RU", { maximumFractionDigits: 0 })} ₽`;
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <AnalyticsFilters
                value={filters}
                onChange={onFiltersChange}
                onRefresh={refresh}
                loading={state.loading}
                games={games}
                gamesLoading={gamesLoading}
                showPeriod
                showGranularity
            />

            <AnalyticsCard
                title="Динамика позиций заявок"
                subtitle="Остаток свободных позиций и уже купленные штуки по заявкам, созданным в периоде."
                snapshotLabel="period"
                howToRead={
                    <Typography level="body-sm" component="div">
                        Остаток — свободные позиции открытых заявок, включая офлайн и выключенные.
                        Куплено — сумма Bought по всем заявкам когорты, даже ещё не закрытым целиком.
                        Переключатель «Количество» / «Сумма ₽» меняет единицу; «час / день / месяц» — шаг оси.
                    </Typography>
                }
                watchFor={
                    <WatchSignals
                        items={[
                            {
                                signal: "Остаток шт.",
                                ifGrows: "копится невыкупленный спрос — смотрите офферы, цены и выключенные заявки.",
                                ifFalls: "остаток сходит в покупки или закрытие заявок.",
                            },
                            {
                                signal: "Куплено шт.",
                                ifGrows: "позиции выкупаются, в том числе из ещё открытых заявок.",
                                ifFalls: "выкуп слабеет относительно созданного спроса.",
                            },
                            {
                                signal: "Доля одной игры в остатке",
                                ifGrows: "ожидаемый рост после акции или обновления игры.",
                                ifFalls: "игра перестаёт кормить спрос — сверьте офферы и цены.",
                            },
                        ]}
                    />
                }
            >
                {state.loading && <BlockLoading />}
                {!state.loading && state.error && <BlockError message={state.error} onRetry={refresh} />}
                {!state.loading && !state.error && (!state.data || state.data.length === 0) && (
                    <EmptyState />
                )}
                {!state.loading && !state.error && state.data && state.data.length > 0 && (
                    <>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                            {totals && (
                                <>
                                    <MetricHint label="Остаток шт." value={formatInt(totals.count)} />
                                    <MetricHint
                                        label="Куплено шт."
                                        value={formatInt(totals.bought)}
                                        color="success"
                                    />
                                    <MetricHint
                                        label="Сумма остатка"
                                        value={formatMoneyKopecks(totals.sum)}
                                        hint="Остаток в деньгах"
                                    />
                                    <MetricHint
                                        label="Сумма купленного"
                                        value={formatMoneyKopecks(totals.boughtSum)}
                                    />
                                </>
                            )}
                        </Box>

                        <ToggleButtonGroup
                            size="sm"
                            value={mode}
                            onChange={(_, v) => v && setMode(v as Mode)}
                            sx={{ alignSelf: "flex-start" }}
                        >
                            <Button value="count">Количество</Button>
                            <Button value="sum">Сумма ₽</Button>
                        </ToggleButtonGroup>

                        <Box sx={{ width: "100%", overflowX: "auto" }}>
                            <Box sx={{ minWidth: isSmall ? Math.max(640, dataset.length * 36) : undefined }}>
                                <LineChart
                                    height={isSmall ? 280 : 360}
                                    dataset={dataset}
                                    xAxis={[
                                        {
                                            dataKey: "x",
                                            scaleType: "time",
                                            valueFormatter: (v: Date) =>
                                                formatBucketLabel(v, filters.granularity),
                                        },
                                    ]}
                                    series={[
                                        {
                                            dataKey: "total",
                                            label: "Остаток",
                                            color: "#1976d2",
                                            showMark: !isSmall,
                                            valueFormatter: valueFmt,
                                        },
                                        {
                                            dataKey: "bought",
                                            label: "Куплено",
                                            color: "#2e7d32",
                                            showMark: !isSmall,
                                            valueFormatter: valueFmt,
                                        },
                                        {
                                            dataKey: "cs",
                                            label: "CS2",
                                            color: "#f57c00",
                                            showMark: false,
                                            valueFormatter: valueFmt,
                                        },
                                        {
                                            dataKey: "dota",
                                            label: "Dota 2",
                                            color: "#c62828",
                                            showMark: false,
                                            valueFormatter: valueFmt,
                                        },
                                        {
                                            dataKey: "tf2",
                                            label: "TF2",
                                            color: "#6a1b9a",
                                            showMark: false,
                                            valueFormatter: valueFmt,
                                        },
                                        {
                                            dataKey: "rust",
                                            label: "Rust",
                                            color: "#8884d8",
                                            showMark: false,
                                            valueFormatter: valueFmt,
                                        },
                                        {
                                            dataKey: "gift",
                                            label: "Gifts",
                                            color: "#2e7d32",
                                            showMark: false,
                                            valueFormatter: valueFmt,
                                        },
                                    ]}
                                    margin={{ left: 60, right: 20, top: 20, bottom: 40 }}
                                />
                            </Box>
                        </Box>
                    </>
                )}
            </AnalyticsCard>
        </Box>
    );
}
